import type { ProspectPreviewContent } from '@/types';

export const PREVIEW_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

function createCatalogSeed() {
  const haircutId = crypto.randomUUID();
  const expressId = crypto.randomUUID();
  const colorId = crypto.randomUUID();
  const haircutCategoryId = crypto.randomUUID();
  const colorCategoryId = crypto.randomUUID();
  return {
    categories: [
      { id: haircutCategoryId, name: 'Haircut', sortOrder: 0 },
      { id: colorCategoryId, name: 'Color', sortOrder: 1 },
    ],
    services: [
      {
        id: haircutId,
        name: 'Signature cut',
        description: 'Personalized cut tailored to each customer.',
        durationMinutes: 45,
        price: 45,
        priceMax: 65,
        categoryId: haircutCategoryId,
        sortOrder: 0,
      },
      {
        id: expressId,
        name: 'Express appointment',
        description: 'A quick refresh when time is limited.',
        durationMinutes: 30,
        price: 30,
        salePrice: 22,
        categoryId: haircutCategoryId,
        sortOrder: 1,
      },
      {
        id: colorId,
        name: 'Full color',
        description: 'Single-process color with a blowout.',
        durationMinutes: 90,
        price: 90,
        categoryId: colorCategoryId,
        sortOrder: 0,
      },
    ],
    bundles: [
      {
        id: crypto.randomUUID(),
        name: 'Cut and color',
        description: 'Signature cut plus full color.',
        categoryId: haircutCategoryId,
        sortOrder: 2,
        serviceIds: [haircutId, colorId],
        salePrice: 120,
      },
    ],
  };
}

export function createProspectPreviewContent(name = 'Your business'): ProspectPreviewContent {
  return {
    businessName: name,
    category: 'Personal care',
    tagline: 'Look your best. Feel your best.',
    description: 'A welcoming local business with skilled professionals and easy online booking.',
    address: 'Your business address',
    phone: '',
    currency: 'USD',
    accentColor: '#288e70',
    logoUrl: '',
    coverUrl: '',
    websiteUrl: '',
    bookingUrl: '',
    instagramUrl: '',
    openingHours: Array.from({ length: 7 }, (_, day) => ({
      day,
      opensAt: '09:00',
      closesAt: '18:00',
      closed: day === 0,
    })),
    sourceMeta: {
      candidateId: null,
      reviewedAt: null,
      fieldSources: {},
      confidence: {},
    },
    ...createCatalogSeed(),
    staff: [
      {
        id: crypto.randomUUID(),
        name: 'First available',
        title: 'Specialist',
        imageUrl: '',
      },
    ],
  };
}

export function previewPublicPath(token: string) {
  return `/preview/${encodeURIComponent(token)}`;
}

export function formatPreviewPrice(price: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `$${price.toFixed(2)}`;
  }
}

export async function readPreviewImage(file: File) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Use a PNG, JPEG, or WebP image.');
  }
  if (file.size > PREVIEW_IMAGE_MAX_BYTES) {
    throw new Error('Image must be smaller than 2 MB.');
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The image could not be read.'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsDataURL(file);
  });
}
